/** The `sessionStorage` key holding the per-tab anonymous session id. */
const SESSION_ID_KEY = 'blufin.telemetry.sessionId';

/** The document foreground/background states this tracker distinguishes (mirrors `DocumentVisibilityState`). */
export type VisibilityState = 'hidden' | 'visible';

/** The payload flushed as the `site.session.ended` event body on tab close. */
export interface SiteSessionEndedPayload {
  duration_seconds: number;
  pages_viewed: number;
}

/** Minimal `sessionStorage` surface — per-tab, cleared when the tab closes. */
export interface ISessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Minimal document surface for reading foreground/background state. */
export interface IVisibilityDocument {
  readonly visibilityState: VisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
}

/** Minimal window surface for the terminal `pagehide` flush. */
export interface IPageLifecycle {
  addEventListener(type: 'pagehide', listener: () => void): void;
}

export interface SiteSessionTrackerOptions {
  /** Monotonic-enough clock (ms); production = `Date.now`. */
  now: () => number;
  /** Called exactly once, on the first `pagehide`, with the finalized session payload. */
  onSessionEnded: (payload: SiteSessionEndedPayload) => void;
  /** Reads/writes the per-tab `sessionId`; production = `window.sessionStorage`. */
  sessionStorage: ISessionStorage;
  /** Foreground/background source; production = `document`. */
  visibilityDocument: IVisibilityDocument;
  /** Terminal-flush source; production = `window`. */
  window: IPageLifecycle;
}

export interface ISiteSessionTracker {
  getSessionId(): string;
  recordPageView(): void;
}

/**
 * Owns the anonymous per-tab session: a `crypto.randomUUID()` id persisted in `sessionStorage`
 * (survives reload, dies with the tab — never a cookie or `localStorage`), a visibility-aware
 * duration accumulator that only counts foreground time, and a `pages_viewed` counter. On the first
 * `pagehide` it seals the accumulator and hands `{ duration_seconds, pages_viewed }` to
 * `onSessionEnded` (the site provider wires that to a `site.session.ended` beacon flush). Idempotent
 * on the terminal flush — a `pagehide`/BFCache-restore/`pagehide` sequence emits exactly once.
 */
export class SiteSessionTracker implements ISiteSessionTracker {
  private _accumulatedMs: number = 0;
  private _ended: boolean = false;
  private readonly _now: () => number;
  private readonly _onSessionEnded: (payload: SiteSessionEndedPayload) => void;
  private _pagesViewed: number = 0;
  private readonly _sessionId: string;
  /** `undefined` while backgrounded; the ms timestamp foreground time started while visible. */
  private _visibleSince: number | undefined;

  /**
   * @param options - Construction options.
   * @param options.now - Monotonic-enough clock (ms); production = `Date.now`.
   * @param options.onSessionEnded - Called once on the first `pagehide` with the sealed payload.
   * @param options.sessionStorage - Per-tab store for the `sessionId`; production = `window.sessionStorage`.
   * @param options.visibilityDocument - Foreground/background source; production = `document`.
   * @param options.window - Terminal-flush source; production = `window`.
   */
  constructor(options: SiteSessionTrackerOptions) {
    this._now = options.now;
    this._onSessionEnded = options.onSessionEnded;
    this._sessionId = this._resolveSessionId(options.sessionStorage);
    if (options.visibilityDocument.visibilityState === 'visible') this._visibleSince = this._now();
    options.visibilityDocument.addEventListener('visibilitychange', () =>
      this._onVisibilityChange(options.visibilityDocument.visibilityState)
    );
    options.window.addEventListener('pagehide', () => this._onPageHide());
  }

  /** The per-tab anonymous session id (stable for the life of the tab). */
  getSessionId(): string {
    return this._sessionId;
  }

  /** Increment the `pages_viewed` counter — called by the provider on each App Router route change. */
  recordPageView(): void {
    this._pagesViewed += 1;
  }

  /** Seal foreground time up to `now`, folding any open visible window into the accumulator. */
  private _accumulateVisible(): void {
    if (this._visibleSince === undefined) return;
    this._accumulatedMs += this._now() - this._visibleSince;
    this._visibleSince = undefined;
  }

  private _onPageHide(): void {
    if (this._ended) return;
    this._ended = true;
    this._accumulateVisible();
    this._onSessionEnded({
      duration_seconds: Math.round(this._accumulatedMs / 1_000),
      pages_viewed: this._pagesViewed
    });
  }

  /**
   * Fold foreground time on background transitions; reopen the visible window on foreground.
   * @param state - The current document visibility state.
   */
  private _onVisibilityChange(state: VisibilityState): void {
    if (state === 'visible') {
      if (this._visibleSince === undefined) this._visibleSince = this._now();
    } else this._accumulateVisible();
  }

  /**
   * Read the existing per-tab `sessionId` or mint + persist a fresh `crypto.randomUUID()`.
   * @param sessionStorage - The per-tab store.
   */
  private _resolveSessionId(sessionStorage: ISessionStorage): string {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const fresh =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  }
}
